import {
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    HostBinding,
    Input,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ModalComponent } from "@app/shared/components";

@Component({
    selector: 'app-wizard-title',
    template: '<ng-content></ng-content>'
})
export class WizardTitleComponent {
}

@Component({
    selector: 'app-wizard-step',
    template: ''
})
export class WizardStepComponent {
    @Input() canContinue = true;
    @Input() isAfterSubmit = false;
    @Input() canSkip = false;
    @Input() canSave = false;
    @Input() saveDisabled = false;
    @Input() showOverlay = false;

    @ContentChild('title', {static: false}) title: TemplateRef<any>;
    @ContentChild('content', {static: false}) content: TemplateRef<any>;

    nextTriggered = false;
    isActive = false;

    reset() {
        this.nextTriggered = false;
    }
}

@Component({
    selector: 'app-wizard-modal',
    templateUrl: 'wizard-modal.component.html',
    styleUrls: ['wizard-modal.component.scss']
})
export class WizardModalComponent {
    @HostBinding('class.vertical') @Input() vertical = false;
    @Input() concludesInDelete = false;
    @Input() canDelete = false;
    @Input() hasDelete = true;
    @Input() canDeleteTooltipText: string;
    @Input() isLoading = false;
    @Input() isEditMode = false;
    @Input() isViewMode = false;
    @Input() isReadOnlyMode = false;
    @Input() hideSteps = false;

    @Output() stepChange = new EventEmitter();
    @Output() stepErrorsChange = new EventEmitter();
    @Output() submit = new EventEmitter();
    @Output() cancel = new EventEmitter();
    @Output() delete = new EventEmitter();

    @Output() modalOpened = new EventEmitter();

    @ViewChild(ModalComponent, {static: false}) modal: ModalComponent;
    @ContentChildren(WizardStepComponent) steps: QueryList<WizardStepComponent>;

    stepList: WizardStepComponent[] = [];
    activeStepIndex = 0;
    private _isOpen = false;
    @HostBinding('class.hide-steps') get hideStepsClass() {
        return this.hideSteps;
    }

    constructor() {
    }

    next() {
        this.activeStep.nextTriggered = true;
        if (!this.activeStep.canContinue) {
            this.stepErrorsChange.emit();
            return;
        }
        this.activeStep.isActive = false;
        this.activeStepIndex++;
        this.stepList = this.steps.toArray();
        this.propagateStepChange();
    }

    prev() {
        this.activeStepIndex--;
        this.propagateStepChange();
    }

    skipStep() {
        this.isLastStep ? this.close() : this.next();
    }

    open() {
        this.stepList = this.steps.toArray();
        this.steps.changes.subscribe(() => {
            this.stepList = this.steps.toArray();
        });

        this.activeStep.isActive = true;
        this.modal.open();
        this._isOpen = true;
    }

    modalOnOpened() {
        this.modalOpened.emit();
    }

    finalSubmit() {
        this.submit.emit();
    }

    onDelete() {
        this.delete.emit();
    }

    onCancelClick() {
        this.cancel.emit();
    }

    close() {
        this.modal.close();
        this._isOpen = false;
    }

    reset() {
        this.activeStepIndex = 0;
        this.steps.forEach(step => step.reset());
    }

    isStepActive(index) {
        return index === this.activeStepIndex;
    }

    isStepComplete(index) {
        return index < this.activeStepIndex;
    }

    propagateStepChange() {
        this.activeStep.isActive = true;
        this.stepChange.emit(this.activeStepIndex);
    }

    get activeStep() {
        return this.stepList[this.activeStepIndex];
    }

    get isLastStep() {
        return this.activeStepIndex === this.stepList.length - 1;
    }

    get isFirstStep() {
        return this.activeStepIndex === 0;
    }

    get nextDisabled() {
        return this.activeStep.nextTriggered ? !this.activeStep.canContinue : false;
    }

    get canSkipStep() {
        return this.activeStep.canSkip;
    }

    get canStepSave() {
        return this.activeStep.canSave;
    }

    get isSaveDisabled() {
        return this.activeStep.saveDisabled;
    }

    get isStepAfterSubmit() {
        return this.activeStep.isAfterSubmit;
    }

    get isOpen() {
        return this._isOpen;
    }
}
